/**
 * Warehouse & Logistics AP tab (ReconciliationHub) — read-only view of the AP
 * subledger for warehouse/freight vendors with attached documents (CDN links)
 * and payment-allocation coverage. Data: /api/warehouse-ap (S7 of the
 * 2026-07-10 warehouse-ap-ledger plan).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Collapse, IconButton, Link, Tooltip,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DescriptionIcon from '@mui/icons-material/Description';
import { getWarehouseApSummary, getWarehouseApVendor } from '../../../services/warehouseApService';

const money = (v, ccy) => `${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} ${ccy || ''}`;

const RAIL_LABELS = { qb_linkedtxn: 'QB', bank_evidence: 'Bank match' };

const PARSE_COLORS = {
  parsed_ok: 'success', parsed_partial: 'warning', mismatch: 'error',
  needs_ocr: 'secondary', linked: 'default', unmatched: 'default', statement: 'info',
};

function DocLinks({ documents }) {
  if (!documents || documents.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box>
      {documents.map((d, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
          <DescriptionIcon sx={{ fontSize: 14 }} color="action" />
          <Link href={d.url} target="_blank" rel="noopener" variant="caption" underline="hover">
            {String(d.file_name).slice(0, 48)}
          </Link>
          <Tooltip title={`${d.source} · ${d.parse_status}`}>
            <Chip size="small" label={d.parse_status} color={PARSE_COLORS[d.parse_status] || 'default'} sx={{ height: 16, fontSize: 10 }} />
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}

function VendorRow({ row, scope }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && !loading) {
      setLoading(true);
      try {
        const res = await getWarehouseApVendor(row.supplier_id, row.currency, scope);
        setDetail(res);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const invoices = detail?.invoices || [];
  const unlinked = detail?.unlinked_documents || [];

  return (
    <>
      <TableRow
        hover
        onClick={toggle}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" tabIndex={-1} aria-label={open ? 'Collapse vendor row' : 'Expand vendor row'}>{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{row.supplier_name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.currency}{Number(row.outstanding_usd) > 0 ? ` · ≈ $${Number(row.outstanding_usd).toLocaleString('en-US', { maximumFractionDigits: 0 })} open` : ''}</Typography>
        </TableCell>
        <TableCell align="right">{row.invoices}</TableCell>
        <TableCell align="right">
          {Number(row.open_invoices) > 0
            ? <Chip size="small" color="warning" label={row.open_invoices} />
            : <Chip size="small" color="success" label="0" />}
        </TableCell>
        <TableCell align="right">{money(row.total_native, row.currency)}</TableCell>
        <TableCell align="right">{money(row.paid_native, row.currency)}</TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={Number(row.outstanding_native) > 0 ? 700 : 400}>
            {money(row.outstanding_native, row.currency)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={`${row.docs_verified} verified / ${row.docs_needs_ocr} need OCR / ${row.docs_unmatched} unmatched`}>
            <span>{row.docs_verified}/{row.docs_total || 0}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{row.alloc_count || 0}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1 }}>
              {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>}
              {error && <Alert severity="error">{error}</Alert>}
              {detail && (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Paid</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Pay allocs</TableCell>
                        <TableCell>Documents</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.slice(0, 100).map(inv => (
                        <TableRow key={inv.id} sx={['unpaid', 'partial'].includes(inv.payment_status) ? { bgcolor: 'warning.50' } : undefined}>
                          <TableCell>{inv.invoice_number}</TableCell>
                          <TableCell>{String(inv.invoice_date).slice(0, 10)}</TableCell>
                          <TableCell align="right">{money(inv.total_amount, inv.currency)}</TableCell>
                          <TableCell align="right">{money(inv.paid_amount, inv.currency)}</TableCell>
                          <TableCell align="right">{money(inv.outstanding_amount, inv.currency)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={inv.payment_status}
                              color={inv.payment_status === 'paid' ? 'success' : (inv.payment_status === 'partial' ? 'warning' : 'error')} />
                          </TableCell>
                          <TableCell align="right">
                            {inv.payments && inv.payments.length > 0 ? (
                              <Tooltip title={
                                <Box>
                                  {inv.payments.slice(0, 8).map((p, i) => (
                                    <div key={i}>{String(p.payment_date).slice(0, 10)} · {Number(p.amount).toLocaleString()} {p.currency} · {p.payment_number} ({RAIL_LABELS[p.source] || p.source})</div>
                                  ))}
                                  {inv.payments.length > 8 && <div>… {inv.payments.length - 8} more</div>}
                                </Box>
                              }>
                                <span
                                  tabIndex={0}
                                  aria-label={`${inv.allocations} payment(s): ${inv.payments.map(p => `${String(p.payment_date).slice(0, 10)} ${Number(p.amount).toLocaleString()} ${p.currency}`).join('; ')}`}
                                  style={{ textDecoration: 'underline dotted', cursor: 'help' }}
                                >{inv.allocations}</span>
                              </Tooltip>
                            ) : inv.allocations}
                          </TableCell>
                          <TableCell><DocLinks documents={inv.documents} /></TableCell>
                        </TableRow>
                      ))}
                      {invoices.length > 100 && (
                        <TableRow><TableCell colSpan={8}>
                          <Typography variant="caption" color="text.secondary">… {invoices.length - 100} more invoices</Typography>
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {unlinked.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {unlinked.length} supplier-level documents not tied to a single invoice (order breakdowns, statements, scans pending OCR):
                      </Typography>
                      <Box sx={{ maxHeight: 180, overflowY: 'auto', mt: 0.5 }}>
                        <DocLinks documents={unlinked.slice(0, 60)} />
                        {unlinked.length > 60 && (
                          <Typography variant="caption" color="text.secondary">… {unlinked.length - 60} more</Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function WarehouseAP() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('logistics');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWarehouseApSummary(scope);
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [scope]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" gutterBottom>Warehouse &amp; Logistics AP</Typography>
        <ToggleButtonGroup
          size="small" exclusive value={scope}
          onChange={(e, v) => { if (v) setScope(v); }}
          aria-label="supplier scope"
        >
          <ToggleButton value="logistics">Logistics only</ToggleButton>
          <ToggleButton value="all">All suppliers</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Accounts payable for warehouses and freight/courier vendors, with attached invoice documents
        (click to open) and exact payment applications. Amounts are in each vendor&apos;s native currency
        (one row per currency); ranking is by USD-equivalent outstanding.
        Document badge: verified = invoice number and exact total confirmed inside the document.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {!loading && !error && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Invoices</TableCell>
                <TableCell align="right">Open</TableCell>
                <TableCell align="right">Total (native)</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Outstanding</TableCell>
                <TableCell align="right">Docs verified</TableCell>
                <TableCell align="right">Pay allocs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => <VendorRow key={`${r.supplier_id}-${r.currency}`} row={r} scope={scope} />)}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No warehouse/logistics vendors found.
                  </Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
